// Shared by the back office and partner portal. The customer space has no SW.
declare const __APP_BUILD_VERSION__: string;

const hashedScriptPath = /^\/(?:assets\/[A-Za-z0-9_-]{8,}|(?:app|portail|espace-client)\/[^/]+-[A-Za-z0-9_-]{8,})\.js$/;

function buildScriptUrl(value: string): URL | null {
  try {
    const url = new URL(value, window.location.href);
    return url.origin === window.location.origin && hashedScriptPath.test(url.pathname) ? url : null;
  } catch {
    return null;
  }
}

function scriptUrlFromError(reason: unknown): URL | null {
  const text = reason instanceof Error ? reason.message : String(reason);
  const candidates = text.match(/(?:https?:\/\/[^\s"'()<>]+|\/[A-Za-z0-9_/-]+)\.js(?:\?[^\s"'()<>]*)?/g) || [];
  for (const candidate of candidates) {
    const url = buildScriptUrl(candidate);
    if (url) return url;
  }
  return null;
}

async function isMissingScript(url: URL): Promise<boolean> {
  try {
    // HEAD bypasses the SW's GET cache. Do not mistake a network outage for a
    // removed file; older servers may still return the SPA's HTML with status 200.
    const response = await fetch(url.href, { method: "HEAD", cache: "no-store" });
    return response.status === 404 || response.status === 410 ||
      (response.ok && (response.headers.get("content-type") || "").includes("text/html"));
  } catch {
    return false;
  }
}

function createBanner(accent: string, onReload: () => void): HTMLElement {
  const banner = document.createElement("div");
  banner.setAttribute("role", "status");
  banner.setAttribute("aria-live", "polite");
  banner.style.cssText = [
    "position:fixed", "left:50%", "bottom:16px", "transform:translateX(-50%)",
    "z-index:2147483647", "display:flex", "align-items:center", "gap:12px",
    "max-width:calc(100vw - 32px)", "padding:12px 16px",
    "border-radius:12px", "background:#fff", "color:#172033",
    "box-shadow:0 8px 32px rgba(0,0,0,.22)", "font:14px system-ui,sans-serif",
  ].join(";");

  const message = document.createElement("span");
  message.textContent = "Nouvelle version disponible";
  message.dataset.updateMessage = "true";
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Recharger";
  button.setAttribute("aria-label", "Recharger pour installer la nouvelle version");
  button.style.cssText = `border:0;border-radius:8px;padding:8px 12px;cursor:pointer;background:${accent};color:#fff;font:600 14px system-ui,sans-serif`;
  button.addEventListener("click", onReload);
  banner.append(message, button);
  return banner;
}

export function registerAppServiceWorker(accent: string): void {
  console.info(`[Jobotech] Version : ${__APP_BUILD_VERSION__}`);

  let registration: ServiceWorkerRegistration | undefined;
  let reloadRequested = false;
  let banner: HTMLElement | undefined;
  const hadController = Boolean(navigator.serviceWorker?.controller);

  const showUpdate = () => {
    if (banner) return;
    banner = createBanner(accent, () => {
      if (!registration?.waiting) {
        window.location.reload(); // Already activated in another tab.
        return;
      }
      reloadRequested = true;
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    });
    document.body.append(banner);
  };

  const checkedScripts = new Map<string, Promise<boolean>>();
  const handleScriptFailure = (url: URL) => {
    let check = checkedScripts.get(url.href);
    if (!check) {
      check = isMissingScript(url).then((missing) => {
        // A temporary failure or an asset still present must be checked again
        // if a later publish removes that same URL.
        if (!missing) checkedScripts.delete(url.href);
        return missing;
      });
      checkedScripts.set(url.href, check);
    }
    void check.then((missing) => {
      if (missing) {
        showUpdate();
        registration?.update().catch(() => {});
      }
    });
  };

  // Resource errors do not bubble: capture scripts and Vite's modulepreload
  // links. Runtime errors in a script are deliberately not treated as updates.
  window.addEventListener("error", (event) => {
    const target = event.target;
    const source = target instanceof HTMLScriptElement ? target.src
      : target instanceof HTMLLinkElement && target.rel === "modulepreload" ? target.href : "";
    const url = source && buildScriptUrl(source);
    if (url) handleScriptFailure(url);
  }, true);

  // Vite emits this for failed dynamic imports, including the on-demand
  // product-import chunk. Keep the original rejection: a HEAD check is async,
  // so we cannot safely suppress errors before knowing the file is missing.
  window.addEventListener("vite:preloadError", (event) => {
    const error = (event as Event & { payload?: unknown }).payload;
    const url = scriptUrlFromError(error);
    if (url) handleScriptFailure(url);
  });
  window.addEventListener("unhandledrejection", (event) => {
    const url = scriptUrlFromError(event.reason);
    if (url) handleScriptFailure(url);
  });

  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadRequested) {
      window.location.reload();
    } else if (hadController) {
      // Another tab activated an update. Never interrupt this tab's draft.
      navigator.serviceWorker.controller?.postMessage({ type: "GET_VERSION" });
    }
  });
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "CHECK_SAFE_UPDATE_CLIENT") {
      event.ports[0]?.postMessage({ type: "SAFE_UPDATE_CLIENT" });
    }
    if (event.data?.type === "UPDATE_BLOCKED") {
      reloadRequested = false;
      showUpdate();
      const text = banner?.querySelector("[data-update-message]");
      if (text) text.textContent = "Fermez les anciens onglets, puis réessayez";
    }
    if (event.data?.type === "SW_VERSION" && event.data.version !== __APP_BUILD_VERSION__) {
      showUpdate();
    }
  });

  window.addEventListener("load", async () => {
    try {
      // Only a worker already waiting when this page opens can activate
      // automatically. An update discovered later must show the banner.
      const previous = await navigator.serviceWorker.getRegistration("/sw.js");
      const waitingAtOpen = Boolean(previous?.waiting);
      registration = await navigator.serviceWorker.register("/sw.js");
      const watchInstallation = (worker: ServiceWorker) => {
        worker.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) showUpdate();
        });
      };
      if (registration.installing) watchInstallation(registration.installing);
      registration.addEventListener("updatefound", () => {
        if (registration?.installing) watchInstallation(registration.installing);
      });
      if (waitingAtOpen && registration.waiting) {
        // This is a new page opening, not a background update of a working tab.
        reloadRequested = hadController;
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      } else if (registration.waiting && hadController) {
        showUpdate();
      }

      const check = () => {
        if (navigator.onLine) registration?.update().catch(() => {});
      };
      window.setInterval(check, 60_000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    } catch (error) {
      console.warn("[Jobotech] Mise à jour du service worker indisponible", error);
    }
  }, { once: true });
}